import { NgTemplateOutlet } from '@angular/common';
import { Component, computed, inject, ChangeDetectionStrategy, viewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatTooltip } from '@angular/material/tooltip';
import { AUTHENTICATED_USER_SERVICE } from '@app/shared/auth/authenticated-user-service/authenticated-user-service.token';
import { HasRoleDirective } from '@app/shared/auth/has-role.directive';
import {
  LucideArchive,
  LucideCornerDownRight,
  LucidePencil,
  LucidePlus,
  LucideTriangleAlert,
} from '@lucide/angular';
import { AnomalyDialog, AnomalyDialogData } from '../anomaly-dialog/anomaly-dialog';
import {
  directionSections,
  directionSubtitle,
  displayedInProgress,
  userSubtitle,
} from '../anomaly-presentation';
import { Anomaly, ANOMALY_AUTHORS, AnomalyDisplayState } from '../anomaly.model';
import { AnomalyService } from '../anomaly.service';

interface MenuItem {
  id: string;
  title: string;
  subtitle: string;
}

interface DirectionItem extends MenuItem {
  anomaly: Anomaly;
  /** L'état du bandeau quand la directrice est elle-même ciblée et l'a réduit ou fermé. */
  reopenable: AnomalyDisplayState | null;
}

const REOPEN_LABELS: Partial<Record<AnomalyDisplayState, string>> = {
  COLLAPSED: 'Déplier mon bandeau, réduit',
  CLOSED: 'Rouvrir mon bandeau, fermé',
};

/**
 * L'icône « Anomalies » de la barre de navigation. Son menu liste les anomalies de l'utilisateur, ou
 * toutes les anomalies pour la direction, qui peut en signaler et consulter les archivées.
 */
@Component({
  selector: 'app-menu-anomalies',
  imports: [
    NgTemplateOutlet,
    MatBadgeModule,
    MatButtonModule,
    MatDividerModule,
    MatMenuModule,
    MatTooltip,
    HasRoleDirective,
    LucideTriangleAlert,
    LucidePencil,
    LucidePlus,
    LucideArchive,
    LucideCornerDownRight,
  ],
  templateUrl: './menu-anomalies.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./menu-anomalies.scss'],
})
export class MenuAnomalies {
  private readonly trigger = viewChild.required<MatMenuTrigger>('trigger');

  /** Ouvre le menu sans clic : une rubrique de l'Espace Pilotage le demande (mode embarqué). */
  open(): void {
    this.trigger().openMenu();
  }

  private readonly anomalyService = inject(AnomalyService);
  private readonly dialog = inject(MatDialog);
  protected readonly authors = ANOMALY_AUTHORS;
  protected readonly isDirection = inject(AUTHENTICATED_USER_SERVICE).hasAnyRole(ANOMALY_AUTHORS);

  private readonly visible = toSignal(this.anomalyService.visible$, { initialValue: [] });
  private readonly all = toSignal(this.anomalyService.all$, { initialValue: [] });

  /** Le menu d'un utilisateur ciblé : ses bandeaux, et l'état où il les a laissés. */
  protected readonly userItems = computed(() => {
    const items = this.visible().map((anomaly) => ({
      id: anomaly.id,
      title: anomaly.title,
      subtitle: userSubtitle(anomaly),
      status: anomaly.status,
    }));
    return {
      inProgress: items.filter((item) => item.status === 'IN_PROGRESS'),
      resolved: items.filter((item) => item.status === 'RESOLVED'),
    };
  });

  /**
   * Le menu de la direction : toutes les anomalies, ciblées ou non sur elle. Quand elle est ciblée et
   * a réduit ou fermé un bandeau, la ligne propose de le rouvrir (RG-anomalie-en-cours-reste-affichee).
   */
  protected readonly directionItems = computed(() => {
    const sections = directionSections(this.all());
    const states = new Map(this.visible().map((v) => [v.id, v.displayState]));
    const items = (anomalies: Anomaly[]): DirectionItem[] =>
      anomalies.map((anomaly) => {
        const state = states.get(anomaly.id);
        return {
          id: anomaly.id,
          title: anomaly.title,
          subtitle: directionSubtitle(anomaly),
          anomaly,
          reopenable: state && state !== 'EXPANDED' ? state : null,
        };
      });
    return {
      inProgress: items(sections.inProgress),
      scheduled: items(sections.scheduled),
      resolved: items(sections.resolved),
      archived: items(sections.archived),
    };
  });

  /** La pastille compte les anomalies en cours qui s'affichent. */
  protected readonly inProgressCount = computed(() =>
    this.isDirection ? displayedInProgress(this.all()).length : this.userItems().inProgress.length,
  );

  protected readonly reopenLabel = (state: AnomalyDisplayState) => REOPEN_LABELS[state] ?? '';

  protected reopen(id: string): void {
    this.anomalyService.setDisplayState(id, 'EXPANDED').subscribe();
  }

  protected openDialog(anomaly?: Anomaly): void {
    this.dialog.open<AnomalyDialog, AnomalyDialogData>(AnomalyDialog, {
      width: '80%',
      minWidth: '320px',
      maxWidth: '760px',
      disableClose: true,
      data: anomaly ? { anomaly } : {},
    });
  }
}
